<template>
  <div class="list">
    <div class="list-head">
      <div>
        <h1>{{ t('app.title') }}</h1>
        <p class="intro">{{ t('app.intro') }}</p>
      </div>
      <button type="button" class="btn primary big" @click="$emit('open', null)">{{ t('list.new') }}</button>
    </div>
    <div v-if="notice" class="notice" :class="notice.type">{{ notice.text }}</div>
    <p v-if="loading" class="muted">{{ t('app.loading') }}</p>
    <p v-else-if="!defs.length" class="muted">{{ t('list.empty') }}</p>
    <div class="cards">
      <div v-for="d in defs" :key="d.id" class="card">
        <div class="card-head">
          <span class="dot" :class="d.enabled ? 'on' : 'off'" />
          <span class="card-title">{{ d.name }}</span>
          <span class="chip">{{ d.enabled ? t('state.enabled') : t('state.disabled') }}</span>
        </div>
        <div class="card-ent" :title="d.rootEntity">
          <div class="card-lbl">{{ t('list.entity') }}</div>
          <div class="card-ent-name mono">
            {{ shortName(d.rootEntity) }}<span v-for="x in d.extensions || []" :key="x" class="ext"> + {{ shortName(x) }}</span>
          </div>
          <dl class="card-key">
            <dt>{{ t('chip.tables') }}</dt>
            <dd class="mono">{{ tablesOf(d) }}</dd>
          </dl>
        </div>
        <div class="card-src">
          <div class="card-lbl">{{ t('list.sources') }}</div>
          <div v-if="!d.sources.length" class="muted sm">{{ t('list.noSources') }}</div>
          <div v-for="s in d.sources" :key="s.questId" class="card-src-item">
            <div class="card-src-row"><span class="mono muted sm">{{ s.questId }}</span> {{ formById(s.questId)?.name || '' }}</div>
            <dl class="card-key">
              <dt>{{ t(s.keyMode === 'entity' ? 'src.keyEntity' : 'chip.keyId') }}</dt>
              <dd v-if="s.idAlias" class="mono">← {{ s.idAlias }}</dd>
              <dd v-else class="warn-tx" :title="t('list.keyMissingHint')">{{ t('list.keyMissing') }}</dd>
              <template v-if="s.keyMode !== 'entity'">
              <dt>{{ t('chip.keyOrigin') }}</dt>
              <dd v-if="s.originAlias">
                <span class="mono">← {{ s.originAlias }}</span>
                <span v-if="s.originText" class="muted sm"> · {{ t('list.keyFallback', { v: s.originText }) }}</span>
              </dd>
              <dd v-else-if="s.originText">{{ t('list.keyFixed', { v: s.originText }) }}</dd>
              <dd v-else class="warn-tx" :title="t('list.keyMissingHint')">{{ t('list.originMissing') }}</dd>
              </template>
            </dl>
          </div>
        </div>
        <div class="card-chips">
          <span class="chip info">{{ triggerText(d) }}</span>
          <span v-if="d.runRequested" class="chip warn">{{ t('state.pendingRun') }}</span>
          <span v-if="d.pending != null" class="chip" :class="d.pending ? 'warn' : 'ok'" :title="t('list.pendingHint')">
            {{ d.pending ? t('list.pending', { n: d.pending }) : t('list.pendingNone') }}
          </span>
          <span class="chip" :title="t('list.countsHint')">{{ t('list.counts', { ok: d.ok, errors: d.errors }) }}</span>
        </div>
        <div class="card-run">
          <div class="card-lbl">{{ t('list.lastRunLbl') }}</div>
          <template v-if="d.running">
            <div class="card-run-head">
              <span class="chip info">{{ t('state.running') }}<span class="dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></span>
              <span v-if="d.runTotal == null" class="sm">{{ t('list.preparing') }}<span class="dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></span>
              <span v-else class="sm">{{ t('list.progress', progressOf(d)) }}</span>
            </div>
            <div class="progress" :class="{ indeterminate: d.runTotal == null }">
              <div class="progress-bar" :style="{ width: progressOf(d).pct + '%' }" />
            </div>
          </template>
          <p v-else-if="!d.lastRunAt" class="muted sm">{{ t('list.neverRun') }}</p>
          <template v-else>
            <div class="card-run-head">
              <span class="chip" :class="stateClass(d)">{{ t('state.' + d.lastRunState) }}</span>
              <span class="mono sm muted">{{ new Date(d.lastRunAt).toLocaleString() }}</span>
            </div>
            <p v-if="d.lastRunMessage" class="card-msg">{{ runSummary(d.lastRunMessage) }}</p>
          </template>
        </div>
        <div class="card-actions">
          <button type="button" class="btn" @click="$emit('open', d.id)">{{ t('list.edit') }}</button>
          <button type="button" class="btn ghost" :disabled="d.running || d.runRequested" @click="run(d)">
            <template v-if="d.running">{{ t('list.running') }}<span class="dots" aria-hidden="true"><i>.</i><i>.</i><i>.</i></span></template>
            <template v-else>{{ d.runRequested ? t('list.queued') : t('list.run') }}</template>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';
import { entityByClass, formById, shortName } from '@vem/catalog.js';
import { listDefs, runDef } from '@vem/api/materializeApi.js';

defineEmits(['open']);

const defs = ref([]);
const loading = ref(true);
const notice = ref(null);

/** Tablas de la raíz y sus extensiones («market · market_medical»). */
const tablesOf = (d) => [d.rootEntity, ...(d.extensions || [])].map((c) => entityByClass(c)?.table).filter(Boolean).join(' · ');

function triggerText(d) {
  if (d.triggerType === 'interval') return t('trigger.every', { n: d.triggerEvery, unit: t('unit.' + d.triggerUnit) });
  if (d.triggerType === 'cron') return 'cron ' + d.triggerCron;
  return t('trigger.' + d.triggerType);
}

/** Resumen de la última ejecución en el idioma de la sesión (el backend lo guarda como JSON de contadores). */
function runSummary(msg) {
  let s;
  try {
    s = JSON.parse(msg);
  } catch {
    return msg;
  }
  if (!s || typeof s !== 'object') return msg;
  const text = !s.pending ? t('list.summaryNone')
    : t('list.summary', { pending: s.pending, created: s.created, updated: s.updated, unchanged: s.unchanged || 0, errors: s.errors });
  return s.fatal ? `${text} · ${t('list.fatal')}: ${s.fatal}` : text;
}

const stateClass = (d) => ({ ok: d.lastRunState === 'ok', warn: d.lastRunState === 'partial', err: d.lastRunState === 'error' });

/** Encuestas tratadas / a tratar de la pasada en curso, con su porcentaje. */
function progressOf(d) {
  const total = d.runTotal || 0;
  const done = Math.min(d.runDone || 0, total);
  return { done, total, pct: total ? Math.round((done * 100) / total) : 0 };
}

let refreshTimer = null;

/** Se recarga sola: a menudo con algo en cola o en curso (progreso), y de vez en cuando para ver las programadas. */
function scheduleRefresh() {
  clearTimeout(refreshTimer);
  const busy = defs.value.some((d) => d.running || d.runRequested);
  if (!busy && notice.value?.text === t('list.runQueued')) {
    notice.value = null;
  }
  refreshTimer = setTimeout(() => load(true), busy ? 3000 : 30000);
}

async function load(silent = false) {
  loading.value = !silent;
  try {
    defs.value = await listDefs();
  } catch (e) {
    notice.value = { type: 'err', text: e.message };
  } finally {
    loading.value = false;
    scheduleRefresh();
  }
}

async function run(d) {
  try {
    await runDef(d.id);
    d.runRequested = true;
    notice.value = { type: 'ok', text: t('list.runQueued') };
    scheduleRefresh();
  } catch (e) {
    notice.value = { type: 'err', text: e.message };
  }
}

onMounted(load);
onBeforeUnmount(() => clearTimeout(refreshTimer));
defineExpose({ load });
</script>

